/**
 * In-Memory adapter with TTL support.
 *
 * Used exclusively by TempService for temporary entities with automatic expiration.
 * Persistent entities (collection, reference, etc.) use Knex SQLite :memory: instead.
 */

import type {
  DatabaseAdapter,
  PaginatedResult,
  ServerEntityQuery
} from '@gzl10/nexus-sdk'
import { applyInMemoryFilters } from './filter-helpers.js'

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20
const DEFAULT_MAX_LIMIT = 10000

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

interface StoredRecord {
  id: string
  created_at: string
  updated_at: string
  [key: string]: unknown
}

interface TtlMetadata {
  expiresAt: number
  timerId: ReturnType<typeof setTimeout>
}

export class InMemoryAdapter implements DatabaseAdapter {
  private tables: Map<string, Map<string, StoredRecord>> = new Map()
  private ttlMap: Map<string, TtlMetadata> = new Map()

  get raw(): unknown {
    return this.tables
  }

  clear(): void {
    for (const metadata of this.ttlMap.values()) {
      clearTimeout(metadata.timerId)
    }
    this.ttlMap.clear()
    this.tables.clear()
  }

  clearTable(table: string): void {
    this.tables.delete(table)
  }

  seed<T extends Record<string, unknown>>(table: string, records: T[]): void {
    const tableData = this.getOrCreateTable(table)
    for (const record of records) {
      const id = (record['id'] as string) || generateId()
      const now = new Date().toISOString()
      tableData.set(id, {
        ...record,
        id,
        created_at: (record['created_at'] as string) || now,
        updated_at: (record['updated_at'] as string) || now
      })
    }
  }

  private getOrCreateTable(table: string): Map<string, StoredRecord> {
    let tableData = this.tables.get(table)
    if (!tableData) {
      tableData = new Map()
      this.tables.set(table, tableData)
    }
    return tableData
  }

  // ---- DatabaseAdapter interface ----

  async findMany<T = unknown>(table: string, query?: ServerEntityQuery): Promise<PaginatedResult<T>> {
    const maxLimit = query?.maxLimit ?? DEFAULT_MAX_LIMIT
    const page = Math.max(1, query?.page ?? DEFAULT_PAGE)
    const limit = Math.min(maxLimit, Math.max(1, query?.limit ?? DEFAULT_LIMIT))
    const offset = (page - 1) * limit

    const tableData = this.tables.get(table)
    if (!tableData) {
      return { items: [], total: 0, page, limit, totalPages: 0, hasNext: false }
    }

    let items = Array.from(tableData.values()) as T[]

    // Apply filters
    if (query?.filters) {
      items = applyInMemoryFilters(items, query.filters)
    }

    // Basic sorting
    if (query?.sort) {
      const sortField = query.sort
      const order = query.order ?? 'asc'
      items.sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[sortField]
        const bVal = (b as Record<string, unknown>)[sortField]
        if (aVal === bVal) return 0
        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1
        const comparison = aVal < bVal ? -1 : 1
        return order === 'asc' ? comparison : -comparison
      })
    }

    const total = items.length
    items = items.slice(offset, offset + limit)
    const totalPages = Math.ceil(total / limit)

    return { items, total, page, limit, totalPages, hasNext: page < totalPages }
  }

  async findOne<T = unknown>(table: string, filters: Record<string, unknown>): Promise<T | null> {
    const tableData = this.tables.get(table)
    if (!tableData) return null
    for (const record of tableData.values()) {
      const match = Object.entries(filters).every(([k, v]) => record[k] === v)
      if (match) return record as T
    }
    return null
  }

  async findById<T = unknown>(table: string, id: string): Promise<T | null> {
    const tableData = this.tables.get(table)
    if (!tableData) return null
    return (tableData.get(id) as T) ?? null
  }

  async count(table: string, filters?: Record<string, unknown>): Promise<number> {
    const tableData = this.tables.get(table)
    if (!tableData) return 0
    if (!filters || Object.keys(filters).length === 0) return tableData.size
    const items = applyInMemoryFilters(Array.from(tableData.values()), filters)
    return items.length
  }

  async insert<T = unknown>(table: string, data: Record<string, unknown>): Promise<T> {
    const tableData = this.getOrCreateTable(table)
    const id = (data['id'] as string) || generateId()
    const now = new Date().toISOString()
    const record: StoredRecord = { ...data, id, created_at: now, updated_at: now }
    tableData.set(id, record)
    return record as T
  }

  async update<T = unknown>(table: string, id: string, data: Record<string, unknown>): Promise<T> {
    const tableData = this.tables.get(table)
    if (!tableData) throw new Error(`Table "${table}" not found`)
    const existing = tableData.get(id)
    if (!existing) throw new Error(`Record "${id}" not found in table "${table}"`)
    const updated: StoredRecord = {
      ...existing, ...data,
      id,
      created_at: existing.created_at,
      updated_at: new Date().toISOString()
    }
    tableData.set(id, updated)
    return updated as T
  }

  async delete(table: string, id: string): Promise<boolean> {
    const tableData = this.tables.get(table)
    if (!tableData) return false
    return tableData.delete(id)
  }

  async transaction<T>(fn: (trx: DatabaseAdapter) => Promise<T>): Promise<T> {
    return fn(this)
  }

  // ---- TTL Methods ----

  private getTtlKey(table: string, id: string): string {
    return `${table}:${id}`
  }

  async insertWithTtl<T = unknown>(table: string, data: Record<string, unknown>, ttlSeconds: number): Promise<T> {
    const tableData = this.getOrCreateTable(table)
    const id = (data['id'] as string) || generateId()
    const now = new Date().toISOString()
    const expires_at = new Date(Date.now() + ttlSeconds * 1000).toISOString()
    const record: StoredRecord = { ...data, id, created_at: now, updated_at: now, expires_at }
    tableData.set(id, record)

    const ttlKey = this.getTtlKey(table, id)
    const timerId = setTimeout(() => this.deleteWithTtlCleanup(table, id), ttlSeconds * 1000)
    this.ttlMap.set(ttlKey, { expiresAt: Date.now() + ttlSeconds * 1000, timerId })

    return record as T
  }

  private deleteWithTtlCleanup(table: string, id: string): void {
    const tableData = this.tables.get(table)
    if (tableData) tableData.delete(id)
    const ttlKey = this.getTtlKey(table, id)
    const metadata = this.ttlMap.get(ttlKey)
    if (metadata) {
      clearTimeout(metadata.timerId)
      this.ttlMap.delete(ttlKey)
    }
  }

  async setTtl(table: string, id: string, ttlSeconds: number): Promise<boolean> {
    const tableData = this.tables.get(table)
    if (!tableData) return false
    const record = tableData.get(id)
    if (!record) return false

    record['expires_at'] = new Date(Date.now() + ttlSeconds * 1000).toISOString()
    record['updated_at'] = new Date().toISOString()

    const ttlKey = this.getTtlKey(table, id)
    const existing = this.ttlMap.get(ttlKey)
    if (existing) clearTimeout(existing.timerId)

    const timerId = setTimeout(() => this.deleteWithTtlCleanup(table, id), ttlSeconds * 1000)
    this.ttlMap.set(ttlKey, { expiresAt: Date.now() + ttlSeconds * 1000, timerId })
    return true
  }

  async getTtl(table: string, id: string): Promise<number> {
    const tableData = this.tables.get(table)
    if (!tableData || !tableData.has(id)) return -2

    const ttlKey = this.getTtlKey(table, id)
    const metadata = this.ttlMap.get(ttlKey)
    if (!metadata) return -1

    const remainingMs = metadata.expiresAt - Date.now()
    return remainingMs <= 0 ? -2 : Math.ceil(remainingMs / 1000)
  }

  async cleanupExpiredIds(_table: string): Promise<number> {
    return 0
  }
}

export function createInMemoryAdapter(): InMemoryAdapter {
  return new InMemoryAdapter()
}
